import { useState, useMemo } from "react";
import { HiInformationCircle, HiArrowRight, HiPencilAlt } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { useTranslation } from "@repo/core";
import { formatPercentage, formatRatio } from "../../../utils/numberFormat";
import { useBenchmarks, useStockDetail } from "../../../hooks/api/useUniversesApi";
import {
  useIntervalEquityData,
  useApiQuery,
  type PortfolioType,
  type EquityIntervalKey,
  type ReturnsSet,
} from "@repo/api";
import { LatestConstituentsList } from "./LatestConstituentsList";

const CHART_PERIODS: EquityIntervalKey[] = ["1M", "3M", "YTD", "ALL"];
const DEFAULT_CHART_PERIOD: EquityIntervalKey = "ALL";

interface StrategyOverviewCardProps {
  portfolio: PortfolioType;
  className?: string;
}

/**
 * Enhanced strategy overview card with comprehensive metrics and performance chart
 */
export const StrategyOverviewCard = ({
  portfolio,
  className = "",
}: StrategyOverviewCardProps) => {
  const navigate = useNavigate();
  const { tCommon, tStrategy, tChart, i18n } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState<EquityIntervalKey>(DEFAULT_CHART_PERIOD);
  const formatMonthYear = (date: string) =>
    new Intl.DateTimeFormat(i18n.language || "en", { month: "long", year: "numeric" }).format(new Date(date));

  // Get multi-interval equity data for this strategy (all periods in one call)
  const { data: intervalEquityData, isLoading: isChartDataLoading } =
    useIntervalEquityData(portfolio.modelId);
  const { data: benchmarks } = useBenchmarks();

  // Fetch current portfolio constituents data directly using modelId
  const { data: portfolioConstituentsData } = useApiQuery<ReturnsSet>({
    path: `/strategy/${portfolio.modelId}/current-returns`,
    queryKey: ["model", "current-returns", portfolio.modelId],
    enabled: !!portfolio.modelId,
  });

  const selectedInterval = intervalEquityData?.intervals?.[selectedPeriod];

  // Extract metrics from the new data structure
  const metrics = useMemo(() => {
    const strategyMetrics = intervalEquityData?.metrics?.Strategy;
    return {
      cagr: strategyMetrics?.CAGR ?? null,
      sharpe: strategyMetrics?.Sharpe ?? null,
      volatility: strategyMetrics?.Volatility ?? null,
    };
  }, [intervalEquityData?.metrics]);

  const handleViewDetails = () => {
    navigate(`/${portfolio.modelId}`);
  };

  const handleViewDraft = () => {
    navigate(`/${portfolio.modelId}/draft`);
  };

  // Check if draft data exists
  const hasDraftData = () => {
    const draft = portfolio?.draft;
    if (!draft) return false;
    // Check if draft has essential properties
    return Boolean(draft.modelId && draft.name);
  };

  // Chart configuration
  const chartOptions: ApexOptions = useMemo(() => {
    return {
      chart: {
        type: "line",
        height: 300,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        background: "transparent",
        defaultLocale: i18n.language === 'cs' ? 'cs' : 'en',
        locales: [
          {
            name: 'en',
            options: {
              months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
              shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
              days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
              shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
              toolbar: {
                download: 'Download SVG',
                selection: 'Selection',
                selectionZoom: 'Selection Zoom',
                zoomIn: 'Zoom In',
                zoomOut: 'Zoom Out',
                pan: 'Panning',
                reset: 'Reset Zoom'
              }
            }
          },
          {
            name: 'cs',
            options: {
              months: ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'],
              shortMonths: ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'],
              days: ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'],
              shortDays: ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'],
              toolbar: {
                download: 'Stáhnout SVG',
                selection: 'Výběr',
                selectionZoom: 'Výběr pro zoom',
                zoomIn: 'Přiblížit',
                zoomOut: 'Oddálit',
                pan: 'Posun',
                reset: 'Reset zoomu'
              }
            }
          }
        ]
      },
      stroke: {
        curve: "straight",
        width: 2,
      },
      colors: ["#3B82F6", "#10B981"],
      xaxis: {
        type: "datetime",
        labels: {
          format: selectedPeriod === "1M" ? "dd MMM" : "MMM yyyy",
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => `$${value.toLocaleString()}`,
        },
      },
      grid: {
        borderColor: "#e5e7eb",
      },
      legend: {
        show: false,
      },
    };
  }, [i18n.language, selectedPeriod]);

  const toSeriesData = (metrics: Record<string, number>) =>
    Object.entries(metrics).map(([date, value]) => ({
      x: new Date(date).getTime(),
      y: value,
    }));

  // Resolve the display name for the "SP500" series the same way
  // strategy-detail does: look it up from /benchmarks (matching either the
  // map key itself or its `universe` short code), falling back to the raw
  // key if the benchmark list hasn't loaded yet or doesn't contain it.
  const sp500Label = useMemo(() => {
    const seriesKey = "SP500";
    // useBenchmarks() returns an array only when called with a filter arg;
    // called bare (as above) it's always the Benchmark map, but the hook's
    // return type is a union of both branches, so narrow it here.
    if (!benchmarks || Array.isArray(benchmarks)) return seriesKey;
    if (benchmarks[seriesKey]) return benchmarks[seriesKey].name || seriesKey;
    const match = Object.entries(benchmarks).find(
      ([, value]) => value.universe?.toUpperCase() === seriesKey
    );
    return match ? match[1].name || seriesKey : seriesKey;
  }, [benchmarks]);

  const chartSeries = useMemo(() => {
    if (!selectedInterval) return [];

    const series = [];

    if (selectedInterval.equity.Strategy) {
      series.push({
        name: portfolio?.name || "Strategy",
        data: toSeriesData(selectedInterval.equity.Strategy),
      });
    }

    if (selectedInterval.equity.SP500) {
      series.push({
        name: sp500Label,
        data: toSeriesData(selectedInterval.equity.SP500),
      });
    }

    return series;
  }, [selectedInterval, portfolio?.name, sp500Label]);

  // Calculate actual value from ALL timeline's last data point (not the selected period)
  const actualValue = useMemo(() => {
    const allInterval = intervalEquityData?.intervals?.ALL;
    const strategyData = allInterval?.equity?.Strategy;
    if (!strategyData) return 0;

    const dates = Object.keys(strategyData).sort();

    if (dates.length === 0) return 0;

    const lastDate = dates[dates.length - 1];
    const lastValue = strategyData[lastDate];

    if (typeof lastValue !== "number" || Number.isNaN(lastValue)) return 0;

    const initialCash =
      portfolio?.backtest?.startCash || intervalEquityData?.start_cash || 100000;

    if (lastValue > 1000) {
      return Math.round(lastValue);
    } else {
      return Math.round(initialCash * (1 + lastValue / 100));
    }
  }, [intervalEquityData, portfolio?.backtest?.startCash]);

  // Extract latest constituents (Long/Short stocks) from portfolio data
  const { latestLongStocks, latestShortStocks, allTickers, latestMonthlyPnL, latestDate } = useMemo(() => {
    const constituentsData = portfolioConstituentsData;

    if (!constituentsData || Object.keys(constituentsData).length === 0) {
      return { latestLongStocks: [], latestShortStocks: [], allTickers: [], latestMonthlyPnL: null, latestDate: null };
    }

    // Get the most recent date's data
    const sortedDates = Object.keys(constituentsData).sort();
    const latestDate = sortedDates[sortedDates.length - 1];
    const latestReturns = constituentsData[latestDate];

    if (!latestReturns) {
      return { latestLongStocks: [], latestShortStocks: [], allTickers: [], latestMonthlyPnL: null, latestDate: null };
    }

    // Transform Longs object to Stock array
    const longStocks = latestReturns.Longs
      ? Object.entries(latestReturns.Longs)
        .sort((a, b) => b[1] - a[1]) // Sort by percentage descending
        .map(([ticker, percentage]) => ({
          id: ticker,
          ticker: ticker,
          companyName: ticker,
          logo: ticker.toLowerCase(),
          percentage: Math.abs(percentage),
          isPositive: percentage >= 0,
        }))
      : [];

    // Transform Shorts object to Stock array
    const shortStocks = latestReturns.Shorts
      ? Object.entries(latestReturns.Shorts)
        .sort((a, b) => b[1] - a[1]) // Sort by percentage descending
        .map(([ticker, percentage]) => ({
          id: ticker,
          ticker: ticker,
          companyName: ticker,
          logo: ticker.toLowerCase(),
          percentage: Math.abs(percentage),
          isPositive: percentage >= 0,
        }))
      : [];

    // Collect all tickers for stock detail lookup
    const tickers = [
      ...longStocks.map((s) => s.ticker),
      ...shortStocks.map((s) => s.ticker),
    ];

    return {
      latestLongStocks: longStocks,
      latestShortStocks: shortStocks,
      allTickers: tickers,
      latestMonthlyPnL: latestReturns.Strategy,
      latestDate: latestDate,
    };
  }, [portfolioConstituentsData]);

  // Fetch stock details (names and logos) for all tickers
  const stockDetailQuery = useStockDetail(allTickers);

  // Transform stock details into lookup map
  const stockDetails = useMemo(() => {
    if (stockDetailQuery.data) {
      return stockDetailQuery.data.reduce(
        (acc, stock) => ({
          ...acc,
          [stock.symbol]: {
            name: stock.name,
            logo: stock.logo,
          },
        }),
        {} as { [key: string]: { name: string; logo: string } }
      );
    }
    return {};
  }, [stockDetailQuery.data]);

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}
    >
      {/* Strategy Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-bold text-gray-900">
              {portfolio.name}
            </h3>
          </div>
          <p className="text-sm text-gray-600">
            {Object.keys(portfolio.factor).join(", ")} | Top {portfolio.backtest?.numberOfStocks || 20} stocks | Leverage {portfolio.backtest?.leverage || 1}x | {portfolio.backtest?.portfolioConstruction === "LongShort" ? `${portfolio.backtest?.longsShare || 50}% for long | ${portfolio.backtest?.shortsShare || 50}% for short` : portfolio.backtest?.portfolioConstruction === "ShortOnly" ? "100% for short" : "100% for long"} | Cap ${(portfolio.backtest?.startCash || 100000).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Work in Progress icon - shows when draft exists */}
          {hasDraftData() && (
            <button
              onClick={handleViewDraft}
              className="flex items-center justify-center p-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors cursor-pointer"
              title="View draft (work in progress)"
            >
              <HiPencilAlt size={18} />
            </button>
          )}

          <button
            onClick={handleViewDetails}
            className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
          >
            <span className="text-sm font-medium">
              {tCommon("viewDetails")}
            </span>
            <HiArrowRight size={16} />
          </button>
        </div>
      </div>

      <hr className="border-gray-200 mb-6" />

      {/* KPI Section and Period Selector */}
      <div className="flex items-end justify-between gap-4 mb-6">
        {/* KPI Metrics */}
        <div className="flex gap-4 flex-wrap flex-1">
          {/* Actual Value */}
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-600">
                {tStrategy("actualValue")}
              </span>
              <div className="relative group">
                <button className="text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer">
                  <HiInformationCircle size={12} />
                </button>
                <div className="hidden group-hover:block group-focus-within:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10 whitespace-nowrap">
                  {tStrategy("tooltip.actualValue")}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </div>
            <div className="text-lg font-bold text-gray-900">
              ${actualValue.toLocaleString()}
            </div>
          </div>

          {/* CAGR */}
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-600">{tCommon("CAGR")}</span>
              <div className="relative group">
                <button className="text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer">
                  <HiInformationCircle size={12} />
                </button>
                <div className="hidden group-hover:block group-focus-within:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10 whitespace-nowrap">
                  Compound Annual Growth Rate
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </div>
            <div
              className={`text-lg font-bold ${
                metrics.cagr !== null && metrics.cagr >= 0 ? "text-green-600" : metrics.cagr !== null ? "text-red-600" : "text-gray-900"
              }`}
            >
              {metrics.cagr !== null ? (
                <>
                  {metrics.cagr >= 0 ? "+" : ""}
                  {formatPercentage(metrics.cagr)}
                </>
              ) : (
                "N/A"
              )}
            </div>
          </div>

          {/* Sharpe Ratio */}
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-600">
                {tStrategy("sharpeRatio")}
              </span>
              <div className="relative group">
                <button className="text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer">
                  <HiInformationCircle size={12} />
                </button>
                <div className="hidden group-hover:block group-focus-within:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10 whitespace-nowrap">
                  {tStrategy("tooltip.sharpeRatio")}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {metrics.sharpe !== null ? formatRatio(metrics.sharpe, 2) : "N/A"}
            </div>
          </div>

          {/* Volatility */}
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-600">
                {tStrategy("volatility")}
              </span>
              <div className="relative group">
                <button className="text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer">
                  <HiInformationCircle size={12} />
                </button>
                <div className="hidden group-hover:block group-focus-within:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10 whitespace-nowrap">
                  {tStrategy("tooltip.volatility")}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {metrics.volatility !== null ? formatPercentage(metrics.volatility, 2) : "N/A"}
            </div>
          </div>

        </div>

        {/* Period Selector */}
        <div className="flex-shrink-0">
          <div className="text-xs text-gray-600 mb-2">
            {tCommon("dateRange")}
          </div>
          <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: "#e5e7eb" }}>
            {CHART_PERIODS.map((period, index) => (
              <button
                key={period}
                type="button"
                onClick={() => setSelectedPeriod(period)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors duration-200 cursor-pointer hover:bg-gray-50 ${index < CHART_PERIODS.length - 1 ? "border-r" : ""
                  }`}
                style={{
                  borderColor: "#e5e7eb",
                  backgroundColor: selectedPeriod === period ? "#F9FAFB" : "white",
                  color: selectedPeriod === period ? "#1A56DB" : "#374151",
                }}
              >
                {period}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div>
        <div className="h-80">
          {isChartDataLoading ? (
            <div className="h-full bg-gray-200 rounded animate-pulse flex items-center justify-center">
              <span className="text-gray-500">Loading chart data...</span>
            </div>
          ) : selectedInterval ? (
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="line"
              height="100%"
            />
          ) : (
            <div className="h-full bg-gray-100 rounded flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="text-4xl mb-2">📊</div>
                <p>{tChart("dataNotAvailable")}</p>
              </div>
            </div>
          )}
        </div>

        {/* Custom Legend */}
        {selectedInterval && (
          <div className="flex items-center gap-6 mt-4 justify-center">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: "#3B82F6" }}
              ></div>
              <span className="text-sm text-gray-700">{portfolio.name}</span>
            </div>
            {selectedInterval.equity.SP500 && (
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: "#10B981" }}
                ></div>
                <span className="text-sm text-gray-700">{sp500Label}</span>
              </div>
            )}
          </div>
        )}

        {/* Monthly PnL */}
        {latestMonthlyPnL !== null && latestDate && (
          <div className="flex items-center justify-start gap-2 mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600">
                {tStrategy("monthlyPnL")} ({formatMonthYear(latestDate)})
              </span>
              <div className="relative group">
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer"
                >
                  <HiInformationCircle size={12} />
                </button>
                <div className="hidden group-hover:block group-focus-within:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10 whitespace-nowrap">
                  {tStrategy("tooltip.monthlyPnL")}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </div>
            <span
              className={`text-lg font-bold ${
                latestMonthlyPnL >= 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {latestMonthlyPnL >= 0 ? "+" : ""}
              {formatPercentage(latestMonthlyPnL)}
            </span>
          </div>
        )}
      </div>

      {/* Latest Constituents Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <LatestConstituentsList
          longStocks={latestLongStocks}
          shortStocks={latestShortStocks}
          stockDetails={stockDetails}
          portfolioConstruction={portfolio?.backtest?.portfolioConstruction}
          initialVisibleCount={5}
        />
      </div>
    </div>
  );
};

export default StrategyOverviewCard;
